'use client';

import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef } from 'react';
import type { RecreateToHomeParams, ViduV1Element } from '@/app/types';

type HomeGenerationSubjectUseHandler = (element: ViduV1Element, traceId?: string) => void;
type HomeGenerationRecreateHandler = (params: RecreateToHomeParams) => boolean;

interface HomeGenerationFormActionsContextValue {
  registerSubjectUseHandler: (handler: HomeGenerationSubjectUseHandler) => () => void;
  useSubjectInHomeForm: HomeGenerationSubjectUseHandler;
  registerRecreateHandler: (handler: HomeGenerationRecreateHandler) => () => void;
  recreateInHomeForm: HomeGenerationRecreateHandler;
}

const HomeGenerationFormActionsContext = createContext<HomeGenerationFormActionsContextValue | undefined>(undefined);

export const HomeGenerationFormActionsProvider = ({ children }: { children: ReactNode }) => {
  const subjectUseHandlerRef = useRef<HomeGenerationSubjectUseHandler | null>(null);
  const recreateHandlerRef = useRef<HomeGenerationRecreateHandler | null>(null);

  const registerSubjectUseHandler = useCallback((handler: HomeGenerationSubjectUseHandler) => {
    subjectUseHandlerRef.current = handler;

    return () => {
      if (subjectUseHandlerRef.current === handler) {
        subjectUseHandlerRef.current = null;
      }
    };
  }, []);

  const registerRecreateHandler = useCallback((handler: HomeGenerationRecreateHandler) => {
    recreateHandlerRef.current = handler;
    return () => {
      if (recreateHandlerRef.current === handler) recreateHandlerRef.current = null;
    };
  }, []);

  const recreateInHomeForm = useCallback<HomeGenerationRecreateHandler>((params) => {
    return recreateHandlerRef.current?.(params) ?? false;
  }, []);

  const useSubjectInHomeForm = useCallback<HomeGenerationSubjectUseHandler>((element, traceId) => {
    subjectUseHandlerRef.current?.(element, traceId);
  }, []);

  const value = useMemo(
    () => ({
      registerSubjectUseHandler,
      useSubjectInHomeForm,
      registerRecreateHandler,
      recreateInHomeForm,
    }),
    [registerSubjectUseHandler, useSubjectInHomeForm, registerRecreateHandler, recreateInHomeForm],
  );

  return (
    <HomeGenerationFormActionsContext.Provider value={value}>{children}</HomeGenerationFormActionsContext.Provider>
  );
};

export const useHomeGenerationFormActions = () => useContext(HomeGenerationFormActionsContext);
